import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useCallback, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

const PT_MONTHS: Record<string, string> = {
  'Janeiro': '01', 'Fevereiro': '02', 'Março': '03', 'Abril': '04',
  'Maio': '05', 'Junho': '06', 'Julho': '07', 'Agosto': '08',
  'Setembro': '09', 'Outubro': '10', 'Novembro': '11', 'Dezembro': '12',
};

function ptDateToISO(ptDate: string): string {
  const match = ptDate.match(/(\d{1,2}) de (\w+) de (\d{4})/);
  if (!match) return '2026-06-27';
  const [, day, monthName, year] = match;
  const month = PT_MONTHS[monthName] || '01';
  return `${year}-${month}-${day.padStart(2, '0')}`;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const navigate = useNavigate();

  const siteUrl = (import.meta as Record<string, unknown>).env
    ? (import.meta as Record<string, { VITE_SITE_URL?: string }>).env.VITE_SITE_URL || 'https://cssbuyspreadsheets.pics'
    : 'https://cssbuyspreadsheets.pics';

  const articleJsonLd = useMemo(() => {
    if (!article) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      'headline': article.title,
      'description': article.intro,
      'image': article.imageUrl,
      'datePublished': ptDateToISO(article.date),
      'dateModified': '2026-06-27',
      'author': {
        '@type': 'Organization',
        'name': 'CSSBuy Spreadsheets',
      },
      'publisher': {
        '@type': 'Organization',
        'name': 'CSSBuy Spreadsheets',
        'logo': {
          '@type': 'ImageObject',
          'url': 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png',
        },
      },
      'mainEntityOfPage': {
        '@type': 'WebPage',
        '@id': `${siteUrl}/artigo/${article.slug}`,
      },
      'inLanguage': 'pt-PT',
    };
  }, [article, siteUrl]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const handleArtigosClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('artigos');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
  }, [navigate]);

  if (!article) {
    return (
      <main>
        <Navbar />
        <section className="pt-40 pb-20 px-4 md:px-6 bg-background-50 text-center">
          <h1 className="text-3xl font-heading font-bold text-foreground-950 mb-4">Artigo não encontrado</h1>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
            Voltar ao Início
          </Link>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <Navbar />

      {/* Article Header with Banner */}
      <section className="relative pt-28 pb-12 md:pb-16 px-4 md:px-6 bg-background-50">
        {/* JSON-LD Structured Data */}
        {articleJsonLd && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
          />
        )}

        {/* Banner Image */}
        <div className="max-w-4xl mx-auto mb-8 rounded-2xl overflow-hidden">
          <div className="w-full h-64 md:h-80">
            <img
              src={article.imageUrl}
              alt={article.imageAlt}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
              Início
            </Link>
            <span>›</span>
            <a href="/#artigos" onClick={handleArtigosClick} className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              Artigos
            </a>
            <span>›</span>
            <span className="text-foreground-800">{article.title}</span>
          </nav>

          {/* Meta */}
          <div className="flex items-center gap-3 mb-5">
            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
              {article.category}
            </span>
            <span className="text-xs text-foreground-500">{article.date}</span>
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium">
              <i className={`${article.tagIcon} w-3 h-3 flex items-center justify-center`} />
              {article.tag}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-extrabold text-foreground-950 leading-tight mb-6">
            {article.title}
          </h1>

          {/* Intro */}
          <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
            {article.intro}
          </p>
        </div>
      </section>

      {/* Article Body */}
      <article className="py-10 px-4 md:px-6 bg-background-50">
        <div className="max-w-3xl mx-auto">
          {article.sections.map((section, idx) => (
            <section key={idx} className="mb-12">
              {section.type === 'text' && (
                <>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                    {section.title}
                  </h2>
                  {section.content.map((p, pi) => (
                    <p key={pi} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {p}
                    </p>
                  ))}
                </>
              )}

              {section.type === 'steps' && (
                <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
                    {section.title}
                  </h2>
                  <ol className="space-y-4">
                    {section.steps?.map((step, si) => (
                      <li key={si} className="flex items-start gap-4">
                        <span className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                          {si + 1}
                        </span>
                        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{step}</p>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {section.type === 'table' && (
                <>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
                    {section.title}
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b border-foreground-200">
                          <th className="text-left py-3 px-4 text-foreground-500 font-medium">Método</th>
                          <th className="text-left py-3 px-4 text-foreground-500 font-medium">Detalhe</th>
                          <th className="text-left py-3 px-4 text-foreground-500 font-medium">Preço</th>
                          <th className="text-left py-3 px-4 text-foreground-500 font-medium">Info Extra</th>
                          <th className="text-left py-3 px-4 text-foreground-500 font-medium">Resultado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {section.table?.map((row, ri) => (
                          <tr key={ri} className={`border-b border-foreground-100 ${ri === 0 ? 'bg-primary-50/50' : ''}`}>
                            <td className={`py-3 px-4 font-semibold ${ri === 0 ? 'text-primary-700' : 'text-foreground-800'}`}>{row.method}</td>
                            <td className="py-3 px-4 text-foreground-700 text-xs md:text-sm">{row.ease}</td>
                            <td className="py-3 px-4 text-foreground-700 text-xs md:text-sm">{row.price}</td>
                            <td className="py-3 px-4 text-foreground-700 text-xs md:text-sm">{row.time}</td>
                            <td className="py-3 px-4 text-foreground-700 text-xs md:text-sm">{row.risk}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {section.type === 'benefits' && (
                <>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
                    {section.title}
                  </h2>
                  <div className="space-y-4">
                    {section.benefits?.map((benefit, bi) => (
                      <div key={bi} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-accent-600 text-sm" />
                        </div>
                        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{benefit}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {section.type === 'highlight' && (
                <div className="bg-accent-50 rounded-2xl p-6 md:p-8">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                    {section.title}
                  </h2>
                  {section.content.map((p, pi) => (
                    <p key={pi} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {p}
                    </p>
                  ))}
                </div>
              )}
            </section>
          ))}

          {/* Conclusion */}
          {article.conclusion.length > 0 && (
            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                Conclusão
              </h2>
              {article.conclusion.map((p, pi) => (
                <p key={pi} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {p}
                </p>
              ))}
            </section>
          )}

          {/* Back to home + CTA */}
          <div className="border-t border-foreground-200 pt-8 mb-8 flex flex-col sm:flex-row items-center gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
              Voltar ao Início
            </Link>
            <a
              href="/#artigos"
              onClick={handleArtigosClick}
              className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-200 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-article-line w-4 h-4 flex items-center justify-center" />
              Mais Artigos
            </a>
          </div>

          {/* CTA to external */}
          <div className="text-center bg-primary-50 rounded-2xl p-8 mb-12">
            <p className="text-lg font-heading font-bold text-foreground-900 mb-4">
              Pronto para encontrar produtos incríveis?
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-accent-500 text-background-50 rounded-full text-base font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Acessar CSSBuy Spreadsheet 2026
              <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center" />
            </a>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}